/**
 * Encerrar e reabrir conversa, na tela (D08 na H04).
 *
 * ── O estado que existe porque o comando é 202 ────────────────────────────
 *
 * A rota confirma que a ENGINE aplicou e devolve `efeitos_pendentes`: o carimbo
 * do encerramento, a atividade na timeline e a tarefa de próxima ação chegam
 * DEPOIS, pelo evento de volta. Então há três estados, e não dois:
 *
 *   parado      nada em curso
 *   enviando    a requisição está no ar
 *   aguardando  a engine confirmou e a projeção ainda não chegou
 *
 * O terceiro é o que a §14.3 exige e o que quase todo produto omite. Pular
 * direto para "encerrada" seria otimismo sobre um efeito que ainda não
 * aconteceu — e se a projeção falhar, a tela mentiria em silêncio.
 *
 * ── Por que a espera tem prazo ────────────────────────────────────────────
 *
 * Fila acumulada, worker reiniciando, SSE caído: o evento pode demorar ou não
 * vir. Um "aguardando" eterno é a mesma mentira do otimismo, só mais lenta.
 * Passado o prazo, o estado vira `demorou` — que não diz que falhou, porque não
 * se sabe: diz que o registro ainda não chegou e que a conversa já foi
 * encerrada no atendimento.
 *
 * ── A chave de idempotência é criada UMA vez por comando ──────────────────
 *
 * Mesma regra do envio (B30): a chave nasce com a intenção, não com a
 * tentativa. Repetir o clique durante o `enviando` não gera comando novo — o
 * botão está desabilitado —, e uma repetição por retry devolveria a primeira
 * resposta em vez de encerrar duas vezes.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import type { ClienteDaApi } from '@cliqcrm/contracts';
import { chaves } from '@/shared/api/chaves';

export type SituacaoDesejada = 'resolvida' | 'aberta';

export type EstadoDoEncerramento =
  | { readonly nome: 'parado' }
  | { readonly nome: 'enviando'; readonly desejada: SituacaoDesejada }
  | { readonly nome: 'aguardando'; readonly desejada: SituacaoDesejada }
  | { readonly nome: 'demorou'; readonly desejada: SituacaoDesejada }
  | { readonly nome: 'falhou'; readonly desejada: SituacaoDesejada; readonly erro: unknown };

/**
 * Quanto tempo esperar a projeção antes de dizer que demorou.
 *
 * Vinte segundos: o caminho normal — webhook, fila, projeção, SSE — fecha em
 * menos de um. Vinte cobre uma fila com acúmulo sem deixar o vendedor olhando
 * para um botão girando enquanto decide o que fazer da vida.
 */
export const PRAZO_DA_CONFIRMACAO_MS = 20_000;

export interface Encerramento {
  readonly estado: EstadoDoEncerramento;
  readonly encerrar: () => void;
  readonly reabrir: () => void;
  /** Volta ao repouso — usado ao dispensar o aviso de falha ou de demora. */
  readonly dispensar: () => void;
}

export function useEncerramento(
  api: ClienteDaApi | null,
  conversaId: string | null,
  /** A situação que a projeção mostra AGORA. É ela que encerra a espera. */
  situacaoProjetada: string | null,
  invalidar: (chaves: readonly (readonly unknown[])[]) => void,
): Encerramento {
  const [estado, definirEstado] = useState<EstadoDoEncerramento>({ nome: 'parado' });

  /*
   * A projeção alcançou o que foi pedido: a espera acabou.
   *
   * Comparar com a situação projetada, e não contar o tempo, é o que faz a tela
   * dizer a verdade — ela sai do "aguardando" quando o fato existe, não quando
   * um cronômetro achou que deveria existir.
   */
  useEffect(() => {
    if (estado.nome !== 'aguardando' && estado.nome !== 'demorou') return;

    const chegou =
      estado.desejada === 'resolvida'
        ? situacaoProjetada === 'resolvida'
        : situacaoProjetada !== null && situacaoProjetada !== 'resolvida';

    if (chegou) definirEstado({ nome: 'parado' });
  }, [estado, situacaoProjetada]);

  /** O prazo da espera. Recriado a cada entrada em `aguardando`. */
  const relogio = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (estado.nome !== 'aguardando') return;

    relogio.current = setTimeout(() => {
      definirEstado((atual) =>
        atual.nome === 'aguardando' ? { nome: 'demorou', desejada: atual.desejada } : atual,
      );
    }, PRAZO_DA_CONFIRMACAO_MS);

    return () => {
      if (relogio.current) clearTimeout(relogio.current);
    };
  }, [estado]);

  const aplicar = useCallback(
    (desejada: SituacaoDesejada) => {
      if (!api || !conversaId) return;

      definirEstado({ nome: 'enviando', desejada });

      void api
        .encerrarConversa({
          caminho: { id: conversaId },
          corpo: { situacao: desejada },
          idempotencyKey: chaveDoComando(),
        })
        .then(() => {
          // A engine confirmou. Ainda NÃO é "encerrada": o carimbo, a atividade
          // e a tarefa chegam pelo evento de volta.
          definirEstado({ nome: 'aguardando', desejada });

          // O SSE também invalida, e depende do fluxo estar de pé. Quem acabou
          // de agir precisa ver o resultado mesmo com o tempo real caído.
          invalidar([chaves.conversa(conversaId), chaves.inbox()]);
        })
        .catch((erro: unknown) => {
          definirEstado({ nome: 'falhou', desejada, erro });
        });
    },
    [api, conversaId, invalidar],
  );

  const encerrar = useCallback(() => aplicar('resolvida'), [aplicar]);
  const reabrir = useCallback(() => aplicar('aberta'), [aplicar]);
  const dispensar = useCallback(() => definirEstado({ nome: 'parado' }), []);

  return { estado, encerrar, reabrir, dispensar };
}

/** Mesma origem da chave do envio: aleatória por comando, não por tentativa. */
function chaveDoComando(): string {
  return globalThis.crypto?.randomUUID?.() ?? `encerrar-${Date.now()}-${Math.random()}`;
}
